import React, { useState, useEffect, useCallback } from 'react';
import { CheckCircle, XCircle, RefreshCw, Eye, EyeOff, Save, AlertTriangle, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

import { API_URL } from '../config';

const FIELD_META = [
  { key: 'META_ACCESS_TOKEN', label: 'Meta Access Token', placeholder: 'EAAxxxxxxx...', help: 'Found in Meta Business Suite → System Users or Graph API Explorer' },
];

const FIELD_GOOGLE = [
  { key: 'GOOGLE_ADS_CLIENT_ID', label: 'Client ID', placeholder: '355530140076-xxxx.apps.googleusercontent.com' },
  { key: 'GOOGLE_ADS_CLIENT_SECRET', label: 'Client Secret', placeholder: 'GOCSPX-xxxx' },
  { key: 'GOOGLE_ADS_DEVELOPER_TOKEN', label: 'Developer Token', placeholder: 'xxxx-xxxx' },
  { key: 'GOOGLE_ADS_CUSTOMER_ID', label: 'Customer ID', placeholder: '1234567890' },
  { key: 'GOOGLE_ADS_REFRESH_TOKEN', label: 'Refresh Token', placeholder: '1//xxxx' },
];

const FIELD_OTHER = [
  { key: 'OPENAI_API_KEY', label: 'OpenAI API Key', placeholder: 'sk-...' },
  { key: 'TELEGRAM_BOT_TOKEN', label: 'Telegram Bot Token', placeholder: '123456:ABC-DEF...' },
  { key: 'EMERGENT_LLM_KEY', label: 'Emergent LLM Key', placeholder: 'emg-...' },
];

const StatusBadge = ({ status }) => {
  if (!status) return (
    <span className="flex items-center gap-1 text-xs text-zinc-500 font-mono">
      <RefreshCw size={12} className="animate-spin" /> Checking...
    </span>
  );
  return status.valid
    ? <span className="flex items-center gap-1.5 text-xs text-green-400 font-mono"><CheckCircle size={13} /> Connected</span>
    : <span className="flex items-center gap-1.5 text-xs text-red-400 font-mono"><XCircle size={13} /> {status.error?.slice(0, 60) || 'Invalid'}</span>;
};

const CredentialSection = ({ title, icon, fields, status, values, onChange, onSave, saving, children }) => {
  const [visible, setVisible] = useState({});

  return (
    <div className="rounded-xl border border-zinc-800 bg-[#09090B] overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-800">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-zinc-800 flex items-center justify-center text-base">{icon}</div>
          <div>
            <div className="text-sm font-semibold text-white font-['Manrope']">{title}</div>
            <StatusBadge status={status} />
          </div>
        </div>
        {status && (
          <div className={`w-2 h-2 rounded-full ${status.valid ? 'bg-green-400' : 'bg-red-500'} shadow-lg ${status.valid ? 'shadow-green-500/30' : 'shadow-red-500/30'}`} />
        )}
      </div>

      {/* Fields */}
      <div className="p-5 space-y-3">
        {fields.map(({ key, label, placeholder, help }) => (
          <div key={key}>
            <label className="block text-xs text-zinc-400 mb-1.5 font-['Inter']">{label}</label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <input
                  type={visible[key] ? 'text' : 'password'}
                  value={values[key] || ''}
                  onChange={e => onChange(key, e.target.value)}
                  placeholder={placeholder}
                  className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-xs font-mono text-zinc-200 placeholder-zinc-600 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500/30 pr-9"
                />
                <button
                  onClick={() => setVisible(v => ({ ...v, [key]: !v[key] }))}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                >
                  {visible[key] ? <EyeOff size={13} /> : <Eye size={13} />}
                </button>
              </div>
            </div>
            {help && <p className="text-[10px] text-zinc-600 mt-1">{help}</p>}
          </div>
        ))}

        <button
          onClick={() => onSave(fields.map(f => f.key))}
          disabled={saving}
          className="mt-2 flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white text-xs font-semibold rounded-lg transition-colors"
        >
          {saving ? <RefreshCw size={12} className="animate-spin" /> : <Save size={12} />}
          Save & Test
        </button>
      </div>
      {children}
    </div>
  );
};

export default function ApiCredentials({ addToast }) {
  const [status, setStatus] = useState(null);
  const [sheetsStatus, setSheetsStatus] = useState(null);
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(null);
  const [checking, setChecking] = useState(false);
  const [connectingSheets, setConnectingSheets] = useState(false);
  const [connectingGoogleAds, setConnectingGoogleAds] = useState(false);

  const getAuthHeader = () => ({
    Authorization: `Bearer ${JSON.parse(localStorage.getItem('signal_auth') || '{}').token}`,
  });

  const fetchSheetsStatus = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/oauth/sheets/status`, { headers: getAuthHeader() });
      const data = await res.json();
      setSheetsStatus(data);
    } catch {}
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchStatus = useCallback(async () => {
    setChecking(true);
    try {
      const res = await fetch(`${API_URL}/credentials/status`, { headers: getAuthHeader() });
      const data = await res.json();
      setStatus(data);
    } catch {
      toast.error('Could not reach backend');
    } finally {
      setChecking(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchStatus();
    fetchSheetsStatus();
  }, [fetchStatus, fetchSheetsStatus]);

  const handleConnectSheets = async () => {
    setConnectingSheets(true);
    try {
      const res = await fetch(`${API_URL}/oauth/sheets/authorize`, { headers: getAuthHeader() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Could not get authorization URL');
      // Open OAuth flow in current window — Google redirects back to /api/oauth/callback
      window.location.href = data.authorization_url;
    } catch (e) {
      toast.error(e.message);
      setConnectingSheets(false);
    }
  };

  const handleConnectGoogleAds = async () => {
    setConnectingGoogleAds(true);
    try {
      const res = await fetch(`${API_URL}/oauth/google-ads/authorize`, { headers: getAuthHeader() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Could not get authorization URL');
      window.location.href = data.authorization_url;
    } catch (e) {
      toast.error(e.message);
      setConnectingGoogleAds(false);
    }
  };

  const handleChange = (key, val) => setValues(v => ({ ...v, [key]: val }));

  const handleSave = async (keys) => {
    const payload = {};
    keys.forEach(k => { if (values[k]) payload[k] = values[k]; });
    if (!Object.keys(payload).length) {
      toast.error('Enter at least one value to update');
      return;
    }
    setSaving(keys[0]);
    try {
      const res = await fetch(`${API_URL}/credentials/update`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${JSON.parse(localStorage.getItem('signal_auth') || '{}').token}`,
        },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Update failed');
      setStatus(data);
      setValues(v => { const next = { ...v }; keys.forEach(k => delete next[k]); return next; });
      const allValid = data.meta?.valid && data.google?.valid;
      toast.success(allValid ? '✅ Credentials updated & verified' : '⚠️ Saved — some APIs still failing');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(null);
    }
  };

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-white font-['Manrope']">API Credentials</h1>
          <p className="text-sm text-zinc-500 mt-0.5">Manage and test your ad platform tokens</p>
        </div>
        <button
          onClick={fetchStatus}
          disabled={checking}
          className="flex items-center gap-2 px-3 py-2 rounded-lg border border-zinc-700 text-zinc-400 hover:text-white hover:border-zinc-500 text-xs transition-colors"
        >
          <RefreshCw size={13} className={checking ? 'animate-spin' : ''} />
          Re-check
        </button>
      </div>

      {/* Overall Status Banner */}
      {status && (!status.meta?.valid || !status.google?.valid) && (
        <div className="flex items-start gap-3 px-4 py-3 rounded-xl bg-red-500/10 border border-red-500/20">
          <AlertTriangle size={16} className="text-red-400 mt-0.5 shrink-0" />
          <div className="text-sm text-red-300 font-['Inter']">
            <span className="font-semibold">API credentials need attention — </span>
            {[
              !status.meta?.valid && 'Meta Ads',
              !status.google?.valid && 'Google Ads',
            ].filter(Boolean).join(' and ')} {' '}
            {(!status.meta?.valid && !status.google?.valid) ? 'are' : 'is'} not working.
            Update your tokens below.
          </div>
        </div>
      )}

      {status && status.meta?.valid && status.google?.valid && (
        <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-green-500/10 border border-green-500/20">
          <CheckCircle size={16} className="text-green-400 shrink-0" />
          <p className="text-sm text-green-300 font-['Inter']">All APIs are connected and working correctly.</p>
        </div>
      )}

      {/* Meta Token Expiry Countdown */}
      {status?.meta?.valid && status.meta.days_until_expiry !== null && status.meta.days_until_expiry !== undefined && (
        <div className={`flex items-center justify-between px-5 py-4 rounded-xl border ${
          status.meta.days_until_expiry <= 7 ? 'bg-red-500/10 border-red-500/30' :
          status.meta.days_until_expiry <= 21 ? 'bg-orange-500/10 border-orange-500/30' :
          'bg-zinc-800/50 border-zinc-700'
        }`}>
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-lg font-bold font-mono ${
              status.meta.days_until_expiry <= 7 ? 'bg-red-500/20 text-red-400' :
              status.meta.days_until_expiry <= 21 ? 'bg-orange-500/20 text-orange-400' :
              'bg-green-500/10 text-green-400'
            }`}>{status.meta.days_until_expiry}</div>
            <div>
              <p className="text-sm font-semibold text-white">days until Meta token expires</p>
              <p className="text-[11px] text-zinc-500">
                {status.meta.days_until_expiry <= 7
                  ? 'Token expiring soon — refresh immediately'
                  : status.meta.days_until_expiry <= 21
                  ? 'Token expiring within 3 weeks'
                  : 'Token is healthy'}
                {status.auto_refresh_available && ' • Auto-refresh enabled'}
              </p>
            </div>
          </div>
          <button
            onClick={async () => {
              try {
                const res = await fetch(`${API_URL}/credentials/refresh-meta`, {
                  method: 'POST',
                  headers: { Authorization: `Bearer ${JSON.parse(localStorage.getItem('signal_auth') || '{}').token}` },
                });
                const data = await res.json();
                if (data.refreshed) {
                  toast.success('Meta token refreshed successfully');
                  fetchStatus();
                } else {
                  toast.error(data.error || 'Could not refresh token');
                }
              } catch { toast.error('Network error'); }
            }}
            className="px-3 py-2 bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-semibold rounded-lg transition-colors flex items-center gap-1.5"
          >
            <RefreshCw size={12} /> Refresh Now
          </button>
        </div>
      )}

      {/* Sections */}
      <CredentialSection
        title="Meta Ads"
        icon="📘"
        fields={FIELD_META}
        status={status?.meta}
        values={values}
        onChange={handleChange}
        onSave={handleSave}
        saving={saving === 'META_ACCESS_TOKEN'}
      />

      <CredentialSection
        title="Google Ads"
        icon="🎯"
        fields={FIELD_GOOGLE}
        status={status?.google}
        values={values}
        onChange={handleChange}
        onSave={handleSave}
        saving={saving === 'GOOGLE_ADS_CLIENT_ID'}
      >
        {status?.google && !status.google.valid && (
          <div className="px-5 pb-4 -mt-1">
            <div className="flex items-center gap-3 p-3 rounded-lg bg-zinc-900/50 border border-zinc-800">
              <AlertTriangle size={16} className="text-amber-400 shrink-0" />
              <p className="text-xs text-zinc-400 flex-1">Token expired? Re-authorize with one click — no manual copy-paste needed.</p>
              <button
                onClick={handleConnectGoogleAds}
                disabled={connectingGoogleAds}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white text-xs font-semibold rounded-lg transition-colors whitespace-nowrap"
              >
                {connectingGoogleAds ? <RefreshCw size={12} className="animate-spin" /> : <ExternalLink size={12} />}
                Re-authorize Google Ads
              </button>
            </div>
          </div>
        )}
      </CredentialSection>

      {/* Google Sheets OAuth Section */}
      <div className="rounded-xl border border-zinc-800 bg-[#09090B] overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-800">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-zinc-800 flex items-center justify-center text-base">📊</div>
            <div>
              <div className="text-sm font-semibold text-white font-['Manrope']">Google Sheets</div>
              {sheetsStatus === null ? (
                <span className="flex items-center gap-1 text-xs text-zinc-500 font-mono"><RefreshCw size={12} className="animate-spin" /> Checking...</span>
              ) : sheetsStatus.configured && sheetsStatus.valid ? (
                <span className="flex items-center gap-1.5 text-xs text-green-400 font-mono"><CheckCircle size={13} /> Connected — lead sheets syncing</span>
              ) : (
                <span className="flex items-center gap-1.5 text-xs text-red-400 font-mono"><XCircle size={13} /> Not connected — sheets sync is broken</span>
              )}
            </div>
          </div>
          {sheetsStatus && (
            <div className={`w-2 h-2 rounded-full ${sheetsStatus.configured && sheetsStatus.valid ? 'bg-green-400 shadow-green-500/30' : 'bg-red-500 shadow-red-500/30'} shadow-lg`} />
          )}
        </div>

        <div className="p-5 space-y-4">
          {sheetsStatus && (!sheetsStatus.configured || !sheetsStatus.valid) && (
            <div className="flex items-start gap-3 px-3 py-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
              <AlertTriangle size={14} className="text-amber-400 mt-0.5 shrink-0" />
              <div className="text-xs text-amber-300 font-['Inter'] space-y-1">
                <p className="font-semibold">Google Sheets needs a one-time authorization</p>
                <p>This uses your Google account to read/write lead sheets. Click "Connect" below, sign in with your Google account, and allow access.</p>
              </div>
            </div>
          )}

          <div className="text-xs text-zinc-500 font-['Inter'] space-y-1">
            <p>Before connecting, make sure this redirect URI is added in your <a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noreferrer" className="text-indigo-400 hover:underline inline-flex items-center gap-0.5">Google Cloud Console <ExternalLink size={10} /></a>:</p>
            <code className="block mt-1 px-3 py-2 bg-zinc-900 rounded-lg text-zinc-300 text-[11px] break-all">
              https://signal.profitcast.com/api/oauth/callback
            </code>
          </div>

          <button
            onClick={handleConnectSheets}
            disabled={connectingSheets}
            className={`flex items-center gap-2 px-4 py-2 text-white text-xs font-semibold rounded-lg transition-colors ${
              sheetsStatus?.configured && sheetsStatus?.valid
                ? 'bg-zinc-700 hover:bg-zinc-600'
                : 'bg-indigo-600 hover:bg-indigo-500'
            } disabled:opacity-50`}
          >
            {connectingSheets ? <RefreshCw size={12} className="animate-spin" /> : <ExternalLink size={12} />}
            {sheetsStatus?.configured && sheetsStatus?.valid ? 'Re-authorize Google Sheets' : 'Connect Google Sheets'}
          </button>
        </div>
      </div>

      <CredentialSection
        title="Other Services"
        icon="⚙️"
        fields={FIELD_OTHER}
        status={{ valid: true }}
        values={values}
        onChange={handleChange}
        onSave={handleSave}
        saving={saving === 'OPENAI_API_KEY'}
      />

      <p className="text-[11px] text-zinc-600 text-center font-mono">
        Credentials are saved to the server's .env file and hot-reloaded instantly — no restart needed.
      </p>
    </div>
  );
}
